import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CounselorNav } from '../counselor-nav/counselor-nav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatBadge } from '@angular/material/badge';
import { MeetingService } from '../../../../services/meeting.service';
import { UserService } from '../../../../services/user.service';
import { Meeting } from '../../../../models/meeting';

@Component({
  selector: 'app-counselor-meetings',
  imports: [
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    CounselorNav,
    RouterModule,
    DatePipe,
    MatBadge,
  ],
  templateUrl: './counselor-meetings.html',
  styleUrl: './counselor-meetings.css',
})
export class CounselorMeetings implements OnInit {
  meetings: Meeting[] = [];

  private meetingService = inject(MeetingService);
  private userService = inject(UserService);
  private cdf = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadMeetings();

    this.meetingService.meetingsChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMeetings());
  }

  private loadMeetings(): void {
    this.meetingService.getMeetings().subscribe({
      next: (meetings) => {
        this.meetings = meetings;
        this.cdf.detectChanges();
      },
    });
  }

  /** Confirmed meetings whose start time is still in the future, soonest first. */
  get upcomingMeetings(): Meeting[] {
    const now = new Date();
    return this.meetings
      .filter((m) => m.status === 'confirmed' && m.start !== null && m.start >= now)
      .sort((a, b) => a.start!.getTime() - b.start!.getTime());
  }

  /**
   * Everything still touching the pending queue — new requests and proposals in
   * either direction, soonest first. Matches the Pending tab's filter, so the
   * badge counts what the tab shows.
   */
  get pendingRequests(): Meeting[] {
    return this.meetings
      .filter((m) => (m.status === 'pending' && !m.isLapsed) || m.hasLiveProposal)
      .sort((a, b) => {
        const aTime = (a.isChangeRequest ? a.proposedStart : a.start)?.getTime() ?? 0;
        const bTime = (b.isChangeRequest ? b.proposedStart : b.start)?.getTime() ?? 0;
        return aTime - bTime;
      });
  }

  /** Count of items actually needing THIS counselor's action right now.
   *  drives the small red "needs attention" badge on the pending tab.
   *  Narrower than pendingRequests. For instance a proposal the counselor made
   *  themselves is visible in the list above, but doesn't need their
   *  attention becuase it's awaiting the student. */
  get actionNeededCount(): number {
    const currentUserId = this.userService.currentUser?.id;
    return this.meetings.filter((m) => m.awaitingResponseUserId === currentUserId).length;
  }

  get navLinks() {
    return [
      { path: '/counselors/meetings/bookings', label: 'Bookings' },
      {
        path: '/counselors/meetings/pending',
        label: 'Pending',
        count: this.actionNeededCount,
      },
      { path: '/counselors/meetings/preferences', label: 'Preferences' },
    ];
  }
}
